import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  getGetProductCategoriesQueryKey,
  getGetProductStatsQueryKey,
  getGetProductsQueryKey,
  useCreateProduct,
  useCreateProductCategory,
  useGetProductCategories,
  useUploadImage,
} from "@/api";
import {
  DEFAULT_PRODUCT_ICON_ID,
  type ProductIconId,
} from "@/lib/products/productIcons";
import {
  createProductFormSchema,
  type CreateProductFormValues,
} from "@/lib/validation/product.schemas";
import { useSessionStore } from "@/stores/session.store";

const NEW_CATEGORY_OPTION_VALUE = "__new__";

export function useCreateProductPage() {
  const { t } = useTranslation(["products", "common"]);
  const queryClient = useQueryClient();
  const activeStoreId = useSessionStore((state) => state.activeStoreId);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [iconId, setIconId] = useState<ProductIconId>(DEFAULT_PRODUCT_ICON_ID);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [showSku, setShowSku] = useState(false);
  const [showDescription, setShowDescription] = useState(false);
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);
  const [createdName, setCreatedName] = useState("");

  const form = useForm<CreateProductFormValues>({
    resolver: zodResolver(createProductFormSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      price: 0,
      stock: 3,
      sku: "",
      description: "",
      is_active: true,
    },
  });

  const { register, control, handleSubmit, reset, setValue, formState } = form;
  const { errors, isValid, dirtyFields } = formState;

  const [
    watchedName,
    watchedPrice,
    watchedStock,
    watchedDescription,
    watchedCategoryId,
    watchedIsActive,
    watchedSku,
  ] = useWatch({
    control,
    name: ["name", "price", "stock", "description", "category_id", "is_active", "sku"],
  });

  const { data: categoriesData } = useGetProductCategories(
    activeStoreId !== null ? { store_id: activeStoreId, per_page: 100 } : undefined,
    { query: { enabled: activeStoreId !== null } },
  );

  const categories = useMemo(
    () => categoriesData?.data?.data ?? [],
    [categoriesData],
  );

  const categoryOptions = useMemo(
    () => [
      ...categories.map((category) => ({
        value: String(category.id),
        label: category.name,
      })),
      {
        value: NEW_CATEGORY_OPTION_VALUE,
        label: t("products:create.category.addNewOption"),
      },
    ],
    [categories, t],
  );

  const selectedCategoryName = useMemo(() => {
    if (!watchedCategoryId) return t("products:create.preview.defaultCategory");
    return categories.find((category) => category.id === watchedCategoryId)?.name ?? "—";
  }, [categories, t, watchedCategoryId]);

  const previewStatus = useMemo(() => {
    if (!watchedIsActive) return { label: t("products:status.inactive"), color: "neutral" as const };
    if (watchedStock <= 0) return { label: t("products:status.outOfStock"), color: "error" as const };
    if (watchedStock <= 5) return { label: t("products:status.lowStock"), color: "warning" as const };
    return { label: t("products:status.inStock"), color: "success" as const };
  }, [t, watchedIsActive, watchedStock]);

  const nameOk = watchedName.trim().length > 0;
  const priceOk = watchedPrice > 0;
  const canSubmit = nameOk && priceOk && isValid;

  const submitHint = useMemo(() => {
    if (nameOk && !priceOk) return t("products:create.submitHintPrice");
    if (!nameOk && priceOk) return t("products:create.submitHintName");
    return t("products:create.submitHint");
  }, [nameOk, priceOk, t]);

  const priceTouched = Boolean(dirtyFields.price);
  const priceFieldState: "default" | "error" = priceTouched && !priceOk ? "error" : "default";
  const priceHelper =
    priceTouched && !priceOk ? t("products:create.priceInvalid") : errors.price?.message;

  const newCategoryTrim = newCategoryName.trim();
  const isDuplicateCategory =
    Boolean(newCategoryTrim) && categories.some((category) => category.name === newCategoryTrim);
  const cannotSaveCategory = !newCategoryTrim || isDuplicateCategory;
  const newCategoryHelper = isDuplicateCategory ? t("products:create.category.duplicate") : "";

  const { mutateAsync: uploadImage, isPending: isUploading } = useUploadImage();

  const { mutate: createProductCategory, isPending: isCreatingCategory } = useCreateProductCategory({
    mutation: {
      onSuccess: (response) => {
        if (response.status !== 201 || activeStoreId === null) return;
        const createdCategory = response.data.data;
        void queryClient.invalidateQueries({
          queryKey: getGetProductCategoriesQueryKey({ store_id: activeStoreId, per_page: 100 }),
        });
        if (createdCategory?.id) {
          setValue("category_id", createdCategory.id, { shouldValidate: true });
        }
        setCategoryModalOpen(false);
        setNewCategoryName("");
      },
    },
  });

  const { mutate: createProduct, isPending: isCreating } = useCreateProduct({
    mutation: {
      onSuccess: (response) => {
        if (response.status !== 201) return;
        void queryClient.invalidateQueries({ queryKey: getGetProductsQueryKey() });
        if (activeStoreId !== null) {
          void queryClient.invalidateQueries({
            queryKey: getGetProductStatsQueryKey({ store_id: activeStoreId }),
          });
        }
        setCreatedName(watchedName || t("products:create.preview.defaultName"));
        setIsSuccess(true);
      },
    },
  });

  const handleImageSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  }, []);

  const toggleIconPicker = useCallback(() => {
    setShowIconPicker((current) => !current);
  }, []);

  const handleSelectIcon = useCallback((nextIconId: ProductIconId) => {
    setIconId(nextIconId);
  }, []);

  const handleShowSku = useCallback(() => {
    setShowSku(true);
  }, []);

  const handleShowDescription = useCallback(() => {
    setShowDescription(true);
  }, []);

  const handleOpenCategoryModal = useCallback(() => {
    setCategoryModalOpen(true);
    setNewCategoryName("");
  }, []);

  const handleCloseCategoryModal = useCallback(() => {
    setCategoryModalOpen(false);
    setNewCategoryName("");
  }, []);

  const handleCategoryChange = useCallback(
    (value: string) => {
      if (value === NEW_CATEGORY_OPTION_VALUE) {
        handleOpenCategoryModal();
        return;
      }
      setValue("category_id", value === "" ? undefined : Number(value), { shouldValidate: true });
    },
    [handleOpenCategoryModal, setValue],
  );

  const handleSaveNewCategory = useCallback(() => {
    if (cannotSaveCategory || activeStoreId === null) return;
    createProductCategory({
      data: {
        store_id: activeStoreId,
        name: newCategoryTrim,
      },
    });
  }, [activeStoreId, cannotSaveCategory, createProductCategory, newCategoryTrim]);

  const handleCreateAnother = useCallback(() => {
    reset({
      name: "",
      price: 0,
      stock: 3,
      sku: "",
      description: "",
      is_active: true,
    });
    setImageFile(null);
    setImagePreview(null);
    setIconId(DEFAULT_PRODUCT_ICON_ID);
    setShowIconPicker(false);
    setShowSku(false);
    setShowDescription(false);
    setIsSuccess(false);
    setCreatedName("");
    setCategoryModalOpen(false);
    setNewCategoryName("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [reset]);

  const onSubmit = useCallback(
    async (values: CreateProductFormValues) => {
      if (activeStoreId === null || !canSubmit) return;

      let imageUrl: string | undefined;
      if (imageFile) {
        const uploadResponse = await uploadImage({ data: { file: imageFile } });
        if (uploadResponse.status === 201) {
          imageUrl = uploadResponse.data.url;
        }
      }

      createProduct({
        data: {
          store_id: activeStoreId,
          name: values.name,
          price: values.price,
          stock: values.stock,
          sku: values.sku || undefined,
          description: values.description || undefined,
          category_id: values.category_id,
          is_active: values.is_active,
          image_url: imageUrl,
        },
      });
    },
    [activeStoreId, canSubmit, createProduct, imageFile, uploadImage],
  );

  const isPending = isCreating || isUploading || isCreatingCategory;

  return {
    mode: "create" as const,
    isLoadingProduct: false,
    isProductError: false,
    form,
    register,
    control,
    handleSubmit,
    errors,
    isValid,
    canSubmit,
    isPending,
    isSuccess,
    createdName,
    fileInputRef,
    imagePreview,
    iconId,
    showIconPicker,
    showSku,
    showDescription,
    categoryModalOpen,
    newCategoryName,
    categoryOptions,
    selectedCategoryName,
    previewStatus,
    watchedName,
    watchedPrice,
    watchedStock,
    watchedDescription,
    watchedSku,
    watchedCategoryId,
    submitHint,
    priceFieldState,
    priceHelper,
    newCategoryHelper,
    cannotSaveCategory,
    isDuplicateCategory,
    handleImageSelect,
    handleImageChange,
    toggleIconPicker,
    handleSelectIcon,
    handleShowSku,
    handleShowDescription,
    handleOpenCategoryModal,
    handleCloseCategoryModal,
    handleCategoryChange,
    handleSaveNewCategory,
    setNewCategoryName,
    handleCreateAnother,
    onSubmit,
  };
}
